// A quién llama el entrenador cuando un chaval no ha llegado — GEMELO en los
// dos repos (`scripts/guard-gemelos-catalogo.mjs`).
//
// EL PROBLEMA, MEDIDO. Hoy, en una sesión, el entrenador ve los nombres y nada
// más: para encontrar un teléfono hay que salir, ir a la lista de alumnos y
// buscarlo — y si un chaval no ha llegado, eso es justo lo que no hay tiempo de
// hacer. Y en 16 de las 52 familias no lo encontraría ni saliendo: el teléfono
// de casa vive en `guardians`, que solo lee su propio tutor o dirección. Por la
// ficha (`profiles.parent_phone`) se llega a 31 de 52.
//
// De ahí la RPC `contacto_de_la_sesion` (mig 332), SECURITY DEFINER: da el
// teléfono y el nombre de quien lo coge, SIN abrir la tabla ni el perfil del
// tutor. Con ella son 47 con teléfono de casa y 52 con alguno.
//
// POR QUÉ ESTO ES UN GEMELO Y NO DOS PANTALLAS PARECIDAS: la sesión del
// entrenador existe en el panel (`MonitorSessionDetailPage`) y en la app
// (`SessionDetailScreen`), y la del móvil es la que más falta hace —el
// entrenador está en el campo, con el teléfono en la mano—. Si el texto se
// escribe dos veces, se separa; y el fichero de al lado
// (`evaluationPendientes.ts`) nació precisamente de eso.
//
// ⚠️ EL TIPO SE ESCRIBE A MANO y no se saca de `database.types.ts`: el
// generador no expresa la nullability de un `RETURNS TABLE`, así que allí las
// seis columnas salen como `string` a secas aunque cuatro puedan ser NULL.
// Poner `| null` en el fichero generado no vale —el gate de tipos lo compara
// con lo que genera la herramienta—, así que la verdad vive aquí.

/** Una fila de `contacto_de_la_sesion` (mig 332). */
export interface ContactoDeAlumno {
  student_id: string;
  student_name: string;
  /** Quien coge el teléfono. NULL si no hay nadie registrado. */
  tutor_nombre: string | null;
  tutor_telefono: string | null;
  /** 'guardians' | 'ficha' | null — de dónde sale el número de casa. */
  de_donde: string | null;
  /** El móvil del propio alumno. Para los 5 que no tienen teléfono de casa. */
  telefono_alumno: string | null;
}

/**
 * A quién se llama, resuelto.
 *
 * `quien` es el ROTULO que ve el entrenador antes de marcar, y por eso dice
 * «(su móvil)» cuando el número es del alumno: llamar al teléfono de un chaval
 * de catorce años creyendo que llamas a su padre es el error que esta pantalla
 * tiene que hacer imposible.
 */
export interface AQuienLlamar {
  telefono: string;
  quien: string;
  /** `true` cuando el número es del propio alumno y no de su casa. */
  esDelAlumno: boolean;
}

/**
 * El teléfono que se marca, con su precedencia: primero el de casa, y el del
 * alumno solo si no hay otro.
 *
 * Devuelve `null` cuando no hay ningún número. Ojo a lo que eso NO significa:
 * un `null` aquí es «esta persona no tiene teléfono registrado», y es distinto
 * de no haber podido leer la RPC. La pantalla tiene que decir las dos cosas con
 * palabras distintas, porque de la segunda sí se puede hacer algo.
 */
export function aQuienLlamar(c: ContactoDeAlumno | undefined): AQuienLlamar | null {
  if (!c) return null;
  const casa = limpia(c.tutor_telefono);
  if (casa) {
    return {
      telefono: casa,
      quien: limpia(c.tutor_nombre) ?? 'Casa',
      esDelAlumno: false,
    };
  }
  const suyo = limpia(c.telefono_alumno);
  if (suyo) {
    // Sin nombre de tutor y con el móvil del chaval, se dice de quién es.
    return { telefono: suyo, quien: 'su móvil', esDelAlumno: true };
  }
  return null;
}

/**
 * El rótulo de la línea de contacto, listo para pintar.
 *
 * `null` cuando no hay a quién llamar: la pantalla escribe entonces su propio
 * «sin teléfono», porque un botón que no aparece no dice que no haya número —no
 * dice nada—, y esa diferencia es la que hace que alguien lo busque fuera o no.
 */
export function rotuloDeContacto(c: ContactoDeAlumno | undefined): string | null {
  const a = aQuienLlamar(c);
  if (!a) return null;
  return a.esDelAlumno ? `${a.telefono} · su móvil` : `${a.quien} · ${a.telefono}`;
}

/** El `href` del enlace. Sin espacios, que algunos marcadores no los tragan. */
export function enlaceTel(telefono: string): string {
  return `tel:${telefono.replace(/[\s()-]/g, '')}`;
}

/** Indexa la respuesta de la RPC por alumno, que es como la pide la lista. */
export function porAlumno(filas: readonly ContactoDeAlumno[]): Record<string, ContactoDeAlumno> {
  const m: Record<string, ContactoDeAlumno> = {};
  for (const f of filas) if (f.student_id) m[f.student_id] = f;
  return m;
}

function limpia(s: string | null | undefined): string | null {
  const t = (s ?? '').trim();
  return t.length > 0 ? t : null;
}
